import {
  LinkableRecordTypesEnum,
  RecordLinkTypeEnum,
} from 'components/framework/Enums';
import { RecordLink } from 'components/framework/models';
import { type LinkableRecordTypesEnum_value } from 'components/framework/models/generated/types';
import type {
  RecordLinkManager as IRecordLinkManager,
  RecordLinkOption,
} from '@aha-app/whiteboard';
import { upperFirst } from 'lodash';

const INVERSE_SUPPORTED_RECORD_LINK_TYPE_TEXT = {
  DEPENDS_ON: 'Is a dependency of',
  IMPACTS: 'Is impacted by',
  BLOCKS: 'Is blocked by',
} as const;

const SUPPORTED_RECORD_LINK_TYPES = [
  'DEPENDS_ON',
  'IMPACTS',
  'BLOCKS',
] as const;

class RecordLinkManager implements IRecordLinkManager {
  // Get the supported link types and their inverse values
  supportedRecordLinkTypes(): string[] {
    return RecordLinkTypeEnum.values
      .filter(value => SUPPORTED_RECORD_LINK_TYPES.includes(value))
      .sort((a, b) => a.localeCompare(b));
  }

  supportedRecordLinkOptions(): RecordLinkOption[] {
    return this.supportedRecordLinkTypes().reduce(
      (
        acc: RecordLinkOption[],
        linkType: Aha.RecordLinkAttributes['linkType']
      ) => {
        const relation = {
          value: linkType,
          text: upperFirst(linkType.toLowerCase().replace(/_/g, ' ')),
          inverse: false,
        };
        const inverseRelation = {
          value: linkType,
          text: INVERSE_SUPPORTED_RECORD_LINK_TYPE_TEXT[linkType],
          inverse: true,
        };
        acc.push(relation, inverseRelation);
        return acc;
      },
      []
    );
  }

  isLinkableRecordType = (
    recordType: string
  ): recordType is LinkableRecordTypesEnum_value =>
    LinkableRecordTypesEnum.values.includes(recordType);

  async createRecordLink({
    parentRecord,
    childRecord,
    linkType,
  }: {
    parentRecord: { id: string; type: string };
    childRecord: { id: string; type: string };
    linkType: Aha.RecordLinkAttributes['linkType'];
  }): Promise<{
    result: boolean;
    recordLink: Aha.RecordLink;
  }> {
    const recordLink = new RecordLink({
      linkType: RecordLinkTypeEnum.RELATES_TO,
    });
    const attributes = { linkType, parentRecord, childRecord };
    const result = await recordLink.save({
      args: { attributes },
      query: recordLink.constructor
        // @ts-expect-error it does exist
        .select(
          'relationDescription',
          'linkType',
          'parentRecordId',
          'childRecordId',
          'inverted'
        )
        .first(),
    });
    // @ts-expect-error This is, in fact, a record link we are returning here
    return { result, recordLink };
  }

  async createRecordLinks({ items, linkType }) {
    const promises = await Promise.all(
      items.map(async ({ parentRecord, childRecord }) => {
        // MutationBatcher does not properly handle queries in batched mutations,
        // so we can't get the errors out of a batched mutation. So, in order to
        // try and display slightly friendlier messages, we'll execute the mutations
        // individually.
        return this.createRecordLink({ parentRecord, childRecord, linkType });
      })
    );
    const results = promises.reduce(
      (acc, result) => {
        acc.result = acc.result && result.result;
        acc.recordLinks.push(result.recordLink);
        return acc;
      },
      { result: true, recordLinks: [] }
    );
    return results;
  }
}

export default RecordLinkManager;
